import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaEnvelope } from 'react-icons/fa';
import { IconType } from 'react-icons';
import logoImage from '../../assets/images/logos/transserrano_viagens_pequeno.png';
import Certifications from '../Certifications';
import { useNewsletter } from '../../contexts/NewsletterContext';

const FooterWrapper = styled.footer`
  background: ${({ theme }) => theme.semantic.color.surface.inverse};
  color: ${({ theme }) => theme.semantic.color.text.inverse};
  padding: ${({ theme }) =>
    `${theme.semantic.space['3xl']} ${theme.semantic.space.xl} ${theme.semantic.space.md}`};
`;

const FooterContent = styled.div`
  max-width: ${({ theme }) => theme.semantic.space.containerMax};
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: ${({ theme }) => theme.semantic.space.xl};
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: ${({ theme }) => theme.semantic.space.xl};
  }

  @media (max-width: ${({ theme }) => theme.primitives.breakpoint.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.semantic.space.xl};
  }
`;

const FooterInfo = styled.div`
  img {
    height: ${({ theme }) => theme.primitives.space[10]};
    width: auto;
    filter: brightness(0) invert(1);
    margin-bottom: ${({ theme }) => theme.semantic.space.md};
  }

  p {
    color: ${({ theme }) => theme.semantic.color.text.inverse};
    opacity: 0.8;
    margin-bottom: ${({ theme }) => theme.semantic.space.sm};
  }

  .tagline {
    font-style: italic;
    font-size: ${({ theme }) => theme.primitives.fontSize.sm};
    margin-top: ${({ theme }) => theme.semantic.space.sm};
  }

  .contact-info {
    margin-top: ${({ theme }) => theme.semantic.space.lg};

    h4 {
      color: ${({ theme }) => theme.semantic.color.text.inverse};
      margin-bottom: ${({ theme }) => theme.primitives.space[3]};
      font-size: ${({ theme }) => theme.primitives.fontSize.base};
    }

    a {
      color: ${({ theme }) => theme.semantic.color.text.inverse};
      font-weight: ${({ theme }) => theme.primitives.fontWeight.semibold};
      text-decoration: none;
      display: inline-block;
      margin-bottom: ${({ theme }) => theme.primitives.space[1]};

      &:hover {
        opacity: 1;
      }
    }

    .note {
      font-size: ${({ theme }) => theme.primitives.fontSize.sm};
      opacity: 0.8;
      margin-bottom: ${({ theme }) => theme.semantic.space.md};
    }
  }

  @media (max-width: ${({ theme }) => theme.primitives.breakpoint.md}) {
    text-align: center;
  }
`;

const FollowSection = styled.div`
  margin-top: ${({ theme }) => theme.semantic.space.xl};

  h4 {
    color: ${({ theme }) => theme.semantic.color.text.inverse};
    margin-bottom: ${({ theme }) => theme.semantic.space.md};
    font-size: ${({ theme }) => theme.primitives.fontSize.base};
  }
`;

const LinkGroup = styled.div`
  h4 {
    color: ${({ theme }) => theme.semantic.color.text.inverse};
    margin-bottom: ${({ theme }) => theme.semantic.space.lg};
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.primitives.space[3]};
  }

  &.legal {
    ul {
      flex-direction: column;
      gap: ${({ theme }) => theme.primitives.space[3]};
    }
    a {
      font-size: ${({ theme }) => theme.primitives.fontSize.sm};
      opacity: 0.8;
    }
  }

  a {
    color: ${({ theme }) => theme.semantic.color.text.inverse};
    text-decoration: none;
    opacity: 0.8;
    transition: opacity ${({ theme }) => theme.primitives.duration.slow}
      ${({ theme }) => theme.primitives.easing.ease};

    &:hover {
      opacity: 1;
    }
  }

  @media (max-width: ${({ theme }) => theme.primitives.breakpoint.md}) {
    text-align: center;
  }
`;

const FooterSocial = styled.div`
  h4 {
    color: ${({ theme }) => theme.semantic.color.text.inverse};
    margin-bottom: ${({ theme }) => theme.semantic.space.lg};
  }

  @media (max-width: ${({ theme }) => theme.primitives.breakpoint.md}) {
    text-align: center;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.semantic.space.md};
  justify-content: flex-start;

  @media (max-width: ${({ theme }) => theme.primitives.breakpoint.md}) {
    justify-content: center;
  }

  a {
    color: ${({ theme }) => theme.semantic.color.text.inverse};
    text-decoration: none;
    width: 35px;
    height: 35px;
    border: 1px solid ${({ theme }) => theme.semantic.color.text.inverse};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
      background-color ${({ theme }) => theme.primitives.duration.normal}
        ${({ theme }) => theme.primitives.easing.ease},
      color ${({ theme }) => theme.primitives.duration.normal}
        ${({ theme }) => theme.primitives.easing.ease};

    &:hover {
      background: ${({ theme }) => theme.semantic.color.text.inverse};
      color: ${({ theme }) => theme.semantic.color.text.primary};
    }

    &:focus-visible {
      outline: none;
      box-shadow: ${({ theme }) => theme.semantic.focusRing};
    }
  }
`;

const FooterBottom = styled.div`
  text-align: center;
  padding-top: ${({ theme }) => theme.semantic.space.xl};
  margin-top: ${({ theme }) => theme.semantic.space['3xl']};
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  p {
    color: ${({ theme }) => theme.semantic.color.text.inverse};
    opacity: 0.7;
  }
`;

const NewsletterButton = styled.button`
  background: ${({ theme }) => theme.semantic.color.brand.primary};
  color: ${({ theme }) => theme.semantic.color.text.inverse};
  border: none;
  padding: ${({ theme }) => `${theme.primitives.space[3]} ${theme.primitives.space[6]}`};
  border-radius: ${({ theme }) => theme.primitives.radius.sm};
  font-size: ${({ theme }) => theme.primitives.fontSize.sm};
  font-weight: ${({ theme }) => theme.primitives.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.semantic.space.sm};
  margin-top: ${({ theme }) => theme.semantic.space.lg};
  width: 100%;
  justify-content: center;
  transition:
    background-color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut},
    transform 80ms ${({ theme }) => theme.primitives.easing.easeOut},
    box-shadow ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut};

  &:hover {
    background: ${({ theme }) => theme.semantic.color.brand.primaryHover};
    box-shadow: ${({ theme }) => theme.primitives.shadow.md};
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.semantic.focusRing};
  }

  svg {
    width: 1em;
    height: 1em;
  }

  @media (max-width: ${({ theme }) => theme.primitives.breakpoint.md}) {
    width: auto;
    margin-left: auto;
    margin-right: auto;
  }
`;

const SocialIcon: React.FC<{ icon: IconType }> = ({ icon: Icon }) => {
  const Component = Icon as React.ComponentType<React.SVGProps<SVGSVGElement>>;
  return <Component />;
};

const Footer: React.FC = () => {
  const { openPopup } = useNewsletter();

  return (
    <FooterWrapper>
      <FooterContent>
        <FooterInfo>
          <img src={logoImage} alt="Transserrano Viagens Logo" />
          <p className="tagline">A Trans Serrano leva-o aonde mais ninguém o leva!</p>

          <div className="contact-info">
            <h4>Contactos</h4>
            <div>
              <a href="tel:+351235778938">+351 235 778 938</a>
              <p className="note">Chamada para a rede fixa nacional</p>
            </div>
            <div>
              <a href="tel:+351966217787">+351 966 217 787</a>
              <p className="note">Chamada para a rede móvel nacional</p>
            </div>
          </div>

          <FollowSection>
            <h4>Siga-nos</h4>
            <SocialLinks>
              <a
                href="https://facebook.com/transserranoviagens"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <SocialIcon icon={FaFacebookF} />
              </a>
              <a
                href="https://www.instagram.com/trans_serrano_viagens/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <SocialIcon icon={FaInstagram} />
              </a>
            </SocialLinks>
          </FollowSection>
        </FooterInfo>

        <LinkGroup>
          <h4>Navegação</h4>
          <ul>
            <li>
              <Link to="/viagens">Viagens</Link>
            </li>
            <li>
              <Link to="/calendario">Calendário</Link>
            </li>
            <li>
              <Link to="/sobre-nos">Sobre</Link>
            </li>
            <li>
              <Link to="/contacto">Contacto</Link>
            </li>
          </ul>
          <NewsletterButton type="button" onClick={openPopup}>
            <SocialIcon icon={FaEnvelope} /> Receba a nossa Newsletter
          </NewsletterButton>
        </LinkGroup>

        <LinkGroup className="legal">
          <h4>Legal</h4>
          <ul>
            <li>
              <Link to="/viagens/condicoes-gerais">Termos e Condições</Link>
            </li>
            <li>
              <Link to="/privacidade">Política de Privacidade</Link>
            </li>
            <li>
              <Link to="/cookies">Política de Cookies</Link>
            </li>
            <li>
              <Link to="/resolucao-conflitos">Resolução de Conflitos</Link>
            </li>
            <li>
              <Link to="/livro-reclamacoes">Livro de Reclamações</Link>
            </li>
            <li>
              <a
                href="/uploads/viagens/pdfs/FICHA-DE-INFORMACAO-NORMALIZADA.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Ficha de Informação Normalizada
              </a>
            </li>
            <li>
              <Link to="/viagens/seguro">Seguro</Link>
            </li>
          </ul>
        </LinkGroup>

        <FooterSocial>
          <Certifications />
        </FooterSocial>
      </FooterContent>

      <FooterBottom>
        <p>
          &copy; {new Date().getFullYear()} Trans Serrano - Aventura, Lazer e Turismo, Lda. Todos os
          direitos reservados | RNAAT_24 | RNAVT_3925
        </p>
      </FooterBottom>
    </FooterWrapper>
  );
};

export default Footer;
